import { RedirectToSignIn, SignedIn, SignedOut } from '@clerk/clerk-react';
import type { ReactNode } from 'react';
import { Route, Routes } from 'react-router-dom';
import { useSessionAnalytics } from './hooks/use-session-analytics';
import { useClerkPostHogBridge, usePageviewTracking } from './lib/posthog';
import { ConnectionsPage } from './pages/ConnectionsPage';
import { DevPage } from './pages/DevPage';
import { GalleryPage } from './pages/GalleryPage';
import { HomePage } from './pages/HomePage';
import { NotFoundPage } from './pages/NotFoundPage';
import { PhotoStudioPage } from './pages/PhotoStudioPage';
import { ProductsPage } from './pages/ProductsPage';
import { SignInPage } from './pages/SignInPage';
import { SignUpPage } from './pages/SignUpPage';
import { useApplyTheme } from './state/theme';

const DEV_ENABLED = import.meta.env.VITE_ENABLE_DEV_ROUTE === 'true';

function Protected({ children }: { children: ReactNode }) {
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
}

export function App() {
  useApplyTheme();
  // Analytics — both hooks are safe no-ops when VITE_POSTHOG_KEY is unset.
  // Pageviews fire on every route change (PostHog autocapture-pageviews
  // is disabled because it only sees full page loads, not SPA navigations).
  // The Clerk bridge binds the user id to PostHog's distinct_id so funnels
  // survive across sessions.
  usePageviewTracking();
  useClerkPostHogBridge();
  useSessionAnalytics();
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/sign-in/*" element={<SignInPage />} />
      <Route path="/sign-up/*" element={<SignUpPage />} />
      <Route
        path="/products"
        element={
          <Protected>
            <ProductsPage />
          </Protected>
        }
      />
      <Route
        path="/gallery"
        element={
          <Protected>
            <GalleryPage />
          </Protected>
        }
      />
      <Route
        path="/connections"
        element={
          <Protected>
            <ConnectionsPage />
          </Protected>
        }
      />
      <Route
        path="/studio/:productUid"
        element={
          <Protected>
            <PhotoStudioPage />
          </Protected>
        }
      />
      {DEV_ENABLED ? <Route path="/dev" element={<DevPage />} /> : null}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
